import { useState } from 'react';
import { isValidVin, normalizeVin } from '@greasemap/core';
import { useVinDecode } from '../../garage/queries';
import { Button, Card, Heading, Spinner, Stack, Text, TextField } from '../../ui';
import type { VinDecode } from '../../garage/types';

/** Props for {@link VinLookupStep}. */
export interface VinLookupStepProps {
  /** Called with the decoded details once the user accepts them. */
  readonly onAccept: (decoded: VinDecode) => void;
  /** Called when the user chooses to type the details instead. */
  readonly onSkip: (vin?: string) => void;
}

/**
 * The first step of adding a vehicle: decode a VIN, or opt out of doing so.
 *
 * The three failure outcomes are kept apart rather than collapsed into one error, because they ask
 * different things of the person. A typo should be corrected here; an unknown vehicle and an
 * outage should both move on to manual entry, but for different reasons and with the VIN retained.
 *
 * @param props - Component props.
 * @returns The VIN lookup step.
 */
export function VinLookupStep({ onAccept, onSkip }: VinLookupStepProps): React.JSX.Element {
  const [vin, setVin] = useState('');
  const normalized = normalizeVin(vin);

  // Only ask the server once the value could possibly be a VIN. The check digit is verified in the
  // browser first, so a typo never costs a round trip.
  const shouldQuery = normalized.length === 17 && isValidVin(normalized);
  const { data, isFetching } = useVinDecode(shouldQuery ? normalized : '');

  const localError =
    normalized.length === 17 && !isValidVin(normalized)
      ? 'That VIN does not pass its check digit, so it contains a typo. Check each character.'
      : undefined;

  return (
    <Card ariaLabel="Find your vehicle by VIN">
      <Stack gap="lg">
        <Stack gap="xs">
          <Heading level={2} size="lg">
            Start with your VIN
          </Heading>
          <Text muted>
            17 characters, usually on the dashboard by the windscreen or inside the driver&apos;s door.
          </Text>
        </Stack>

        <TextField
          label="VIN"
          value={vin}
          onChange={(event) => setVin(event.target.value)}
          maxLength={17}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          error={localError}
          hint="We use it to fill in the year, make and model for you."
        />

        {isFetching && (
          <Stack direction="row" gap="sm" align="center">
            <Spinner label="Looking up your VIN" />
            <Text muted>Looking up your VIN…</Text>
          </Stack>
        )}

        {data !== undefined && data.status === 'Decoded' && (
          <Stack gap="md">
            <Text>
              Found a <strong>{[data.year, data.make, data.model, data.trim].filter(Boolean).join(' ')}</strong>
              {data.bodyClass === null || data.bodyClass === undefined ? '' : ` (${data.bodyClass})`}.
            </Text>
            <Stack direction="row" gap="sm" wrap>
              <Button variant="primary" onClick={() => onAccept(data)}>
                Yes, that is my car
              </Button>
              <Button onClick={() => onSkip(normalized)}>Not quite, let me edit</Button>
            </Stack>
          </Stack>
        )}

        {data !== undefined && data.status === 'NotFound' && (
          <Stack gap="md">
            <Text>
              That VIN looks valid, but the vehicle database has no record of it. You can enter the
              details yourself.
            </Text>
            <Button variant="primary" onClick={() => onSkip(normalized)}>
              Enter the details
            </Button>
          </Stack>
        )}

        {data !== undefined && data.status === 'ProviderUnavailable' && (
          <Stack gap="md">
            <Text>The vehicle database is not responding right now. Nothing is lost.</Text>
            <Button variant="primary" onClick={() => onSkip(normalized)}>
              Enter the details instead
            </Button>
          </Stack>
        )}

        <Stack direction="row" gap="sm">
          <Button onClick={() => onSkip()}>I do not have the VIN</Button>
        </Stack>
      </Stack>
    </Card>
  );
}
